import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './ProgressModal.css';

interface ProgressModalProps {
    isOpen: boolean;
    currentTask: string;
    currentIndex: number;
    totalTasks: number;
    taskType?: 'article' | 'quick_win' | 'image_selection' | 'general';
    /** Optional concrete quick_win_type ('email_newsletter', 'social_media_posts', etc.)
     *  If provided, overrides the name-pattern heuristic for the heading. */
    quickWinType?: string | null;
    onClose?: () => void;
}

const ProgressModal: React.FC<ProgressModalProps> = ({
    isOpen,
    currentTask,
    currentIndex,
    totalTasks,
    taskType = 'general',
    quickWinType = null,
    onClose
}) => {
    const startTimeRef = useRef<number | null>(null);
    const [eta, setEta] = useState<string>('');

    // Track start time when modal opens; block navigation while active
    useEffect(() => {
        if (isOpen && startTimeRef.current === null) {
            startTimeRef.current = Date.now();
        }
        if (!isOpen) {
            startTimeRef.current = null;
            setEta('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isOpen]);

    // Recalculate ETA whenever currentIndex or totalTasks changes
    useEffect(() => {
        if (!isOpen || totalTasks === 0) { setEta(''); return; }

        let remainingSecs: number;

        if (currentIndex === 0 || !startTimeRef.current) {
            // No completions yet — seed from 3 min per article estimate
            remainingSecs = totalTasks * 3 * 60;
        } else {
            // Use actual elapsed rate once articles start completing
            const elapsed = (Date.now() - startTimeRef.current) / 1000;
            const avgPerTask = elapsed / currentIndex;
            remainingSecs = Math.round(avgPerTask * (totalTasks - currentIndex));
        }

        if (remainingSecs <= 0) { setEta(''); return; }
        if (remainingSecs < 60) setEta(`~${remainingSecs}s remaining`);
        else setEta(`~${Math.ceil(remainingSecs / 60)}m remaining`);
    }, [currentIndex, isOpen, totalTasks]);

    if (!isOpen) return null;

    const progressPercentage = totalTasks > 0 ? (currentIndex / totalTasks) * 100 : 0;

    const getProgressHeading = () => {
        if (taskType === 'image_selection') return 'Selecting Hero Images';

        // Prefer the explicit quick_win_type if available (data-driven)
        if (quickWinType) {
            switch (quickWinType) {
                case 'email_newsletter':     return 'Generating Email';
                case 'social_media_posts':
                case 'social_media':         return 'Generating Social Media Posts';
                case 'meta_data':            return 'Generating SEO Metadata';
                case 'ad_copy':              return 'Generating Ad Copy';
                case 'keyword_research':     return 'Generating Keyword Research';
                case 'content_calendar':     return 'Generating Content Calendar';
            }
        }

        if (taskType === 'article') return 'Generating Articles';
        if (taskType === 'quick_win') {
            const t = currentTask.toLowerCase();
            if (t.includes('social media') || t.includes('social_media')) return 'Generating Social Media Posts';
            if (t.includes('meta') || t.includes('seo')) return 'Generating SEO Metadata';
            if (t.includes('ad copy') || t.includes('ad_copy')) return 'Generating Ad Copy';
            if (t.includes('keyword')) return 'Generating Keyword Research';
            if (t.includes('email') || t.includes('newsletter')) return 'Generating Email';
            return 'Generating Quick Wins';
        }
        return 'Processing Tasks';
    };

    const cleanTaskName = (task: string): string =>
        task.replace(/Generate\s+article:\s*/gi, '').trim();

    const isActive = currentIndex < totalTasks;

    return ReactDOM.createPortal(
        <div className="progress-modal-overlay">
            <div className="progress-modal">
                <div className="progress-modal-header">
                    <div className="progress-modal-title-row">
                        <h3 className="progress-modal-title">{getProgressHeading()}</h3>
                        {onClose && (
                            <button className="progress-modal-close" onClick={onClose}>✕</button>
                        )}
                    </div>
                    <div className="progress-modal-subtitle">
                        <span className="progress-counter">{currentIndex} of {totalTasks} complete</span>
                        {eta && <span className="progress-eta">{eta}</span>}
                    </div>
                </div>

                <div className="progress-modal-body">
                    {/* Progress bar */}
                    <div className="progress-track">
                        <div
                            className="progress-fill"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                    <div className="progress-pct">{Math.round(progressPercentage)}%</div>

                    {/* Current task */}
                    <div className={`progress-current-task${isActive ? ' is-active' : ''}`}>
                        {isActive && <span className="progress-writing-dot" />}
                        <span className="progress-task-label">
                            {isActive
                                ? `Generating: ${cleanTaskName(currentTask) || '…'}`
                                : 'All done!'}
                        </span>
                    </div>

                    {/* Stay-on-page notice */}
                    {isActive && (
                        <div className="progress-stay-notice">
                            Please keep this tab open — navigating away will interrupt generation.
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ProgressModal;
